/**
 * Time-off requests sheet — pending approvals + full history.
 *
 * Iter 145 — now lists EVERY request (pending / approved / rejected) with
 * status tabs, so admin-entered time-off (auto-approved) is visible too.
 * Reads GET /api/admin/time-off/pending?status=all and writes via
 * PATCH /api/clinicians/{cid}/blocks/{bid}/status.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { CheckCircle2, XCircle, RefreshCw, PlaneTakeoff, AlertCircle, Trash2 } from 'lucide-react';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

export default function AdminTimeOff() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/time-off/pending', { params: { status: 'all' } });
      setRows(r.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not load requests.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: rows.length };
    rows.forEach(r => { const s = r.status || 'approved'; if (c[s] !== undefined) c[s] += 1; });
    return c;
  }, [rows]);

  const visible = useMemo(
    () => tab === 'all' ? rows : rows.filter(r => (r.status || 'approved') === tab),
    [rows, tab]);

  const act = async (row, status, note = null) => {
    setBusy(row.block_id);
    setMsg('');
    try {
      await api.patch(`/clinicians/${row.clinician_id}/blocks/${row.block_id}/status`, { status, note });
      setMsg(`${status === 'approved' ? 'Approved' : 'Rejected'} — ${row.clinician_name || 'clinician'}'s request.`);
      setRejecting(null); setRejectNote('');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || `Could not ${status} request.`);
    } finally { setBusy(null); }
  };

  const remove = async (row) => {
    if (!(await safeConfirm('Remove this time-off entry? The clinician becomes bookable again for these dates.'))) return;
    setBusy(row.block_id);
    try {
      await api.delete(`/clinicians/${row.clinician_id}/blocks/${row.block_id}`);
      setMsg('Time-off entry removed.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not remove entry.');
    } finally { setBusy(null); }
  };

  const badge = (s) => s === 'approved'
    ? <span className="badge-success text-[10px]">approved</span>
    : s === 'rejected'
      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">rejected</span>
      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">pending</span>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-time-off-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#003D80] font-medium">Approvals</div>
          <h1 className="text-3xl font-heading font-bold mt-1 flex items-center gap-3" data-testid="admin-time-off-title">
            <PlaneTakeoff className="w-7 h-7 text-[#003D80]" />
            Time-off requests
          </h1>
          <p className="text-sm text-fg-muted mt-1">Every time-off entry (whether the clinician requests it, or you enter it on their Working hours page) lands here as <strong>Pending</strong>. Approve it to block those dates in the booking calendar; reject it to keep them bookable. Approved entries can be removed at any time.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-soft text-sm hover:bg-soft" data-testid="reload-time-off">
          <RefreshCw className="w-4 h-4" /> Reload
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-1.5 text-sm rounded-md ${tab === t.key ? 'bg-brand text-white' : 'border border-soft hover:bg-soft'}`}
                  data-testid={`timeoff-tab-${t.key}`}>
            {t.label} <span className="opacity-70">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      {msg && (
        <div className="flex items-start gap-2 border border-soft bg-soft/40 rounded-md p-3" data-testid="admin-time-off-msg">
          <AlertCircle className="w-4 h-4 text-[#003D80] mt-0.5" />
          <span className="text-sm text-fg">{msg}</span>
        </div>
      )}

      {loading ? (
        <div className="text-fg-muted py-8 text-center">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-fg-muted py-12 text-center border border-dashed border-soft rounded-lg" data-testid="admin-time-off-empty">
          {tab === 'pending' ? 'No pending time-off requests. All caught up.' : `No ${tab === 'all' ? '' : tab + ' '}time-off entries yet.`}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(r => (
            <div key={r.block_id} className="border border-soft rounded-lg p-4 flex items-start gap-4" data-testid={`timeoff-row-${r.block_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-fg flex items-center gap-2">
                  {r.clinician_name || r.clinician_id} {badge(r.status || 'approved')}
                </div>
                <div className="text-xs text-fg-muted mt-0.5">
                  {new Date(r.starts_at).toLocaleString()} → {new Date(r.ends_at).toLocaleString()}
                </div>
                {r.reason && <div className="text-sm text-fg mt-2">{r.reason}</div>}
                {r.status_note && <div className="text-xs text-fg-muted mt-1 italic">Note: {r.status_note}</div>}
                <div className="text-[11px] text-fg-muted mt-1">
                  Submitted {new Date(r.created_at).toLocaleString()}
                  {r.reviewed_at ? ` · reviewed ${new Date(r.reviewed_at).toLocaleString()}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(r.status || 'approved') === 'pending' ? (
                  <>
                    <button disabled={busy === r.block_id} onClick={() => { setRejecting(r); setRejectNote(''); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-60"
                            data-testid={`reject-${r.block_id}`}>
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button disabled={busy === r.block_id} onClick={() => act(r, 'approved')}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002A5A] disabled:opacity-60"
                            data-testid={`approve-${r.block_id}`}>
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                  </>
                ) : (
                  <button disabled={busy === r.block_id} onClick={() => remove(r)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-soft text-sm text-[#B83A3A] hover:bg-red-50 disabled:opacity-60"
                          data-testid={`remove-${r.block_id}`}>
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center pt-24 px-4" onClick={() => setRejecting(null)} data-testid="reject-modal">
          <div className="bg-white border border-soft rounded-xl w-full max-w-md shadow-xl p-5" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold mb-1">Reject time-off request</h3>
            <p className="text-xs text-fg-muted mb-3">{rejecting.clinician_name} · {new Date(rejecting.starts_at).toLocaleDateString()} → {new Date(rejecting.ends_at).toLocaleDateString()}</p>
            <label className="block mb-4">
              <div className="text-xs text-fg-muted mb-1">Optional note to the clinician (why rejected)</div>
              <textarea className="input min-h-[70px] !text-sm" value={rejectNote} onChange={e => setRejectNote(e.target.value)} data-testid="reject-note-input"/>
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary text-sm" onClick={() => setRejecting(null)}>Cancel</button>
              <button className="px-4 py-2 rounded-md bg-[#B83A3A] text-white text-sm font-semibold" disabled={busy === rejecting.block_id}
                      onClick={() => act(rejecting, 'rejected', rejectNote.trim() || null)} data-testid="reject-confirm-btn">
                Reject request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
